import { supabase } from "@/lib/supabase";
import {
  applyPreviewResultToDraftSession,
  createImageAccessState,
  mapImageGenerationErrorBody,
} from "@/features/image-generation/imageGenerationMappers";
import type {
  ConfirmImagePreviewInput,
  ConfirmImagePreviewResult,
  GenerateImagePreviewInput,
  GenerateImagePreviewResult,
  ImageGenerationDraftSession,
} from "@/features/image-generation/imageGeneration.types";

type FunctionErrorBody = {
  cacheStatus?: "hit" | "invalidated" | "miss" | "none" | "not-applicable" | null;
  code?: string;
  creditCost?: number | null;
  creditsRemaining?: number | null;
  creditsSafe?: boolean;
  currentBalance?: number | null;
  message?: string;
  recommendedTier?: "author" | "crafter" | "free" | null;
  resetAt?: string | null;
  retryAfterSeconds?: number | null;
  stage?: string | null;
  tier?: "author" | "crafter" | "free" | null;
  truncated?: boolean | null;
};

async function normalizeFunctionError(error: unknown) {
  if (!error || typeof error !== "object") {
    return {
      code: "IMAGE_GENERATION_FAILED",
      message: "IMAGE_GENERATION_FAILED",
    } satisfies FunctionErrorBody;
  }

  if ("context" in error && typeof error.context === "object" && error.context) {
    const context = error.context as { json?: (() => Promise<FunctionErrorBody>) | FunctionErrorBody };
    if (typeof context.json === "function") {
      try {
        return await context.json();
      } catch {
        // Fall back to the generic error message below.
      }
    } else if (context.json) {
      return context.json;
    }
  }

  return {
    code: "message" in error ? String(error.message) : "IMAGE_GENERATION_FAILED",
    message: "message" in error ? String(error.message) : "IMAGE_GENERATION_FAILED",
  } satisfies FunctionErrorBody;
}

export async function generateImagePreview(
  input: GenerateImagePreviewInput,
): Promise<GenerateImagePreviewResult> {
  const result = await supabase.functions.invoke("generate-image", {
    body: input,
  });

  if (result.error) {
    throw mapImageGenerationErrorBody(await normalizeFunctionError(result.error));
  }

  const data = result.data as Partial<GenerateImagePreviewResult> | null;
  const platformStage =
    data && typeof data === "object" && "platformRestriction" in data
      ? (((data as { platformRestriction?: { stage?: string | null } | null }).platformRestriction)?.stage ?? null)
      : null;
  const fallbackStage =
    data && typeof data === "object" && "stage" in data && typeof data.stage === "string"
      ? data.stage
      : null;

  return {
    accessState: createImageAccessState({
      cacheStatus:
        data?.cacheStatus === "hit"
        || data?.cacheStatus === "miss"
        || data?.cacheStatus === "none"
        || data?.cacheStatus === "invalidated"
        || data?.cacheStatus === "not-applicable"
          ? data.cacheStatus
          : null,
      code: "available",
      creditCost: data?.creditCost,
      creditsRemaining: data?.creditsRemaining ?? null,
      stage: platformStage ?? fallbackStage,
      truncated: data?.truncated ?? false,
    }),
    charged: data?.charged ?? false,
    citations: data?.citations ?? [],
    creditCost: data?.creditCost ?? 8,
    creditsRemaining: data?.creditsRemaining ?? null,
    previewUrl: data?.previewUrl ?? "",
    settlementPhase: data?.settlementPhase ?? "approval",
    tempAssetRef: data?.tempAssetRef ?? "",
  };
}

export async function confirmImagePreview(
  input: ConfirmImagePreviewInput,
): Promise<ConfirmImagePreviewResult> {
  const result = await supabase.functions.invoke("confirm-image", {
    body: input,
  });

  if (result.error) {
    throw mapImageGenerationErrorBody(await normalizeFunctionError(result.error));
  }

  return (result.data ?? {}) as ConfirmImagePreviewResult;
}

export async function requestImagePreviewForDraft(
  session: ImageGenerationDraftSession,
) {
  const preview = await generateImagePreview({
    projectId: session.projectId,
    prompt: session.editedPrompt,
    sourceId: session.sourceId,
    sourceKind: session.sourceKind,
    useLiveModel: true,
  });

  return applyPreviewResultToDraftSession(session, preview);
}
